import React, { useEffect, useState } from 'react';
import PageHeading from '../components/Common/PageHeading';
import { useTranslation } from 'react-i18next';
import StarbeatHistoryCard from '../components/Card/StarbeatHistoryCard';
import useGetBeatInfo from '../hooks/useGetBeatInfo';
import EmptyContent from '../components/Common/EmptyContent';
import { useInView } from 'react-intersection-observer';
import ERROR_CODES from '../constants/errorCodes';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { loadingSpinnerState } from '../recoil/loadingSpinner/loadingSpinnerAtom';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { otpModalState } from '../recoil/otp/otpModalAtom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CountUp from 'react-countup';
import * as Sentry from '@sentry/react';
import { countryState } from '../recoil/country/countryAtom';

// Day.js Timezone 관련 플러그인 활성화
dayjs.extend(utc);
dayjs.extend(timezone);

const StarbeatHistoryList = () => {
  const [ref, inView] = useInView();
  const setOtpLogin = useSetRecoilState(otpModalState);
  const accessToken = localStorage.getItem('access_token');
  const countryData = useRecoilValue(countryState);
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const activeTab = 'all';
  const { beatInfo } = useGetBeatInfo(accessToken, countryData?.country);
  const setIsLoading = useSetRecoilState(loadingSpinnerState);
  const [histories, setHistories] = useState([]);
  const [nextKey, setNextKey] = useState(0);

  // 접속 국가에 따른 타임존 설정
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const clearSession = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('exp');
    Sentry.setUser(null);
  };

  const fetchHistories = async () => {
    setIsLoading(true);

    let key_param = '';
    if (nextKey !== null && nextKey !== 0) {
      key_param = `?next_key=${nextKey}`;
    }
    try {
      const accessToken = localStorage.getItem('access_token');

      const response = await api.get(`beat/starbeat/history${key_param}`, {
        headers: {
          Authorization: `${accessToken}`,
        },
      });
      const newHistories = response?.data?.histories;

      if (newHistories && newHistories.length > 0) {
        const combinedHistories = [...histories, ...newHistories];
        setHistories(combinedHistories);
        const newNextKey = response?.data?.next_key;
        if (nextKey !== null) {
          setNextKey(newNextKey);
        }
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
        navigate('/error');
      }

      const errorCode = error.response && error.response.data && error.response.data.code;
      if (
        errorCode === ERROR_CODES.invalidToken ||
        errorCode === ERROR_CODES.tokenExpired ||
        errorCode === ERROR_CODES.unauthorized
      ) {
        clearSession();
        navigate('/');
        setOtpLogin(true); // 로그인 모달
      } else {
        console.log(errorCode);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const groupedPurchaseList = () => {
    // 날짜를 기준으로 내림차순 데이터 정렬
    const sortedData = histories.slice().sort((a, b) => new Date(b.buy_at) - new Date(a.buy_at));

    // 날짜를 기준으로 데이터 그룹화
    const groupedData = [];
    let currentDate = null;

    sortedData.forEach((item) => {
      const buy_at_divider = dayjs(item.buy_at).tz(timezone).format('YYYY. MM. DD'); // 날짜 추출
      // 추후 탭 기능 붙일 때 사용 가능
      if (activeTab === 'all' || item.platform === activeTab) {
        if (buy_at_divider !== currentDate) {
          // 새로운 날짜 그룹
          currentDate = buy_at_divider;
          groupedData.push({ buy_at_divider: currentDate, items: [item] });
        } else {
          // 기존 날짜 그룹
          groupedData[groupedData.length - 1].items.push(item);
        }
      }
    });

    return groupedData;
  };

  useEffect(() => {
    fetchHistories();
  }, []);

  useEffect(() => {
    // inView: 스크롤이 하단에 닿았을 때 true
    if (inView) {
      if (nextKey) fetchHistories();
    }
  }, [inView]);

  return (
    <div className={'ml-page'}>
      <PageHeading headingTxt={t('star_beat_history')} />
      <div className='layout-inner'>
        <div className='page-layout-wrapper'>
          <div className={'my-starbeat-wrapper type-history-page'}>
            <div className='my-starbeat-left-wrapper'>
              <p className='my-starbeat-info-label'>{t('my_star_beats')}</p>
              <div className='my-starbeat-icon-info-group'>
                <div className='my-starbeat-icon-group'>
                  <i className='icon icon-star-point' />
                </div>
                <span className='my-starbeat-info'>
                  {beatInfo?.data?.star_beat ? <CountUp end={beatInfo?.data?.star_beat} duration={1} /> : '0'}
                </span>
              </div>
            </div>
          </div>

          <div className='purchase-history-list-all-wrapper'>
            <div className='purchase-list-wrapper'>
              <div className='table-content-wrapper'>
                <div className='table-content-thead-wrapper table-layout-wrapper mb-hide'>
                  <div className='table-content-thead-left-wrapper table-layout-left-wrapper'>
                    <div className={'table-content-thead-group table-layout-center'}>
                      <span className={'table-content-thead-txt'}>{t('product_information')}</span>
                    </div>
                  </div>
                  <div className='table-content-thead-right-wrapper table-layout-right-wrapper'>
                    <div className={'table-content-thead-group table-layout-center table-layout-content-group'}>
                      <span className={'table-content-thead-txt'}>{t('date')}</span>
                    </div>
                    <div className={'table-content-thead-group table-layout-center table-layout-content-group'}>
                      <span className={'table-content-thead-txt'}>{t('quantity')}</span>
                    </div>
                  </div>
                </div>
                <div className='table-content-tbody-wrapper'>
                  {groupedPurchaseList().length > 0 ? (
                    groupedPurchaseList().map((group, groupIndex) => (
                      <div key={groupIndex} className={'table-content-group-wrapper'}>
                        <span style={{ color: '#fff' }} className={'mobile-date-divider mb-show'}>
                          {group.buy_at_divider}
                        </span>
                        {group.items.map((item) => (
                          <StarbeatHistoryCard key={item.pk} item={item} />
                        ))}
                      </div>
                    ))
                  ) : (
                    <EmptyContent emptyText={t('no_star_beat_history')} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <span ref={ref} style={{ opacity: 0 }} />
    </div>
  );
};

export default StarbeatHistoryList;
